import { Duration, Instant, LocalDate } from "@js-joda/core";
import { Caregiver } from "./Caregiver.ts";
import {
  PersonalizedDiabetesProtocol,
  PersonalizedHypertensionProtocol,
  toPersonalizedDiabetesProtocol,
  toPersonalizedHypertensionProtocol,
} from "./ClinicalProtocol.ts";
import Department from "./Department.ts";
import { Medication, toMedication } from "./Medication";
import {
  DiabetesPatientApiDto,
  hasDiabetes,
  hasHypertension,
  HypertensionPatientApiDto,
} from "./PatientApiDto";
import PatientStatus from "./PatientStatus";

export type Patient = {
  id: string;
  medicalRecordNumber: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  estimatedDateOfDelivery?: LocalDate;
  deliveryDate?: LocalDate;
  diagnosis?: string;
  lastReviewTime?: Instant;
  firstReadingTime?: Instant;
  status: PatientStatus;
  prescribingCaregiverDepartment?: Department;
  prescribingCaregiver: Caregiver;
  lastLiveInteractionTime?: Instant;
  educationProvidedAt?: Instant;
  stickyNote: string;
  clinicalTimeForCurrentMonth: Duration;
};

type HypertensionFields = {
  prescriptionId: string;
  unreviewedReadings: UnreviewedHypertensionReadings;
  personalizedClinicalProtocol: PersonalizedHypertensionProtocol;
};

type DiabetesFields = {
  prescriptionId: string;
  unreviewedReadings: UnreviewedDiabetesReadings;
  personalizedClinicalProtocol: PersonalizedDiabetesProtocol;
  medications: Medication[];
};

export type HypertensionPatient = Patient & HypertensionFields & { hasDiabetes: boolean };
export type DiabetesPatient = Patient & DiabetesFields & { hasHypertension: boolean };

export type UnreviewedHypertensionReadings = {
  total: number;
  low: number;
  acceptable: number;
  elevated: number;
  critical: number;
};

export type UnreviewedDiabetesReadings = {
  total: number;
  emergencyLow: number;
  criticalLow: number;
  low: number;
  acceptable: number;
  elevated: number;
  criticalHigh: number;
  pending: number;
  unclassified: number;
};

export const toHypertensionPatient = <T>(
  dto: T & HypertensionPatientApiDto
): T & HypertensionPatient => ({
  ...dto,
  estimatedDateOfDelivery: parseLocalDate(dto.estimatedDateOfDelivery),
  lastLiveInteractionTime: parseInstant(dto.lastLiveInteractionTime),
  ...dto.hypertension,
  personalizedClinicalProtocol: toPersonalizedHypertensionProtocol(
    dto.hypertension.personalizedClinicalProtocol
  ),
  lastReviewTime: parseInstant(dto.hypertension.lastReviewTime),
  firstReadingTime: parseInstant(dto.hypertension.firstReadingTime),
  hasDiabetes: hasDiabetes(dto),
  deliveryDate: parseLocalDate(dto.hypertension.deliveryDate),
  educationProvidedAt: parseInstant(dto.hypertension.educationProvidedAt),
  clinicalTimeForCurrentMonth: Duration.ofSeconds(dto.clinicalTimeForCurrentMonthInSeconds),
});

export const toDiabetesPatient = <T>(dto: T & DiabetesPatientApiDto): T & DiabetesPatient => ({
  ...dto,
  estimatedDateOfDelivery: parseLocalDate(dto.estimatedDateOfDelivery),
  lastLiveInteractionTime: parseInstant(dto.lastLiveInteractionTime),
  ...dto.diabetes,
  personalizedClinicalProtocol: toPersonalizedDiabetesProtocol(
    dto.diabetes.personalizedClinicalProtocol
  ),
  lastReviewTime: parseInstant(dto.diabetes.lastReviewTime),
  firstReadingTime: parseInstant(dto.diabetes.firstReadingTime),
  hasHypertension: hasHypertension(dto),
  deliveryDate: parseLocalDate(dto.diabetes.deliveryDate),
  medications: (dto.diabetes.prescribedMedications ?? []).map(toMedication),
  educationProvidedAt: parseInstant(dto.diabetes.educationProvidedAt),
  clinicalTimeForCurrentMonth: Duration.ofSeconds(dto.clinicalTimeForCurrentMonthInSeconds),
});

const parseInstant = (instant: string | undefined) =>
  instant ? Instant.parse(instant) : undefined;

const parseLocalDate = (localDate: string | undefined) =>
  localDate ? LocalDate.parse(localDate) : undefined;

export type PatientOffboardingReason =
  | "device-connectivity-issue"
  | "patient-completed-protocol"
  | "patient-end-program-early"
  | "patient-non-compliant"
  | "patient-transferred"
  | "patient-declined"
  | "other";

export type OffboardingRequest = {
  prescriptionId: string;
  reason: PatientOffboardingReason;
  deliveryDate?: LocalDate;
  note?: string;
};
